import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ScrollReveal from "@/components/ScrollReveal";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, CheckCircle2, Eye, EyeOff, Clock, RefreshCw, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AttestationPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFull, setShowFull] = useState(false);

  const fetchAttestation = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attestation`);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttestation();
  }, []);

  const truncateHash = (hash) => {
    if (!hash) return "";
    return `${hash.slice(0, 12)}...${hash.slice(-12)}`;
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short",
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#050B14]">
      <div className="noise-overlay" aria-hidden="true" />

      {/* Top Bar */}
      <div className="border-b border-slate-800 bg-[#050B14]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <button
            data-testid="attestation-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-serif text-lg font-bold text-white">JCAM</span>
          </button>
          <span className="text-xs tracking-[0.15em] uppercase text-slate-500">
            Fiduciary Attestation
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37]/30 mb-6">
              <Shield className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-3">
              Fiduciary Attestation
            </p>
            <h1
              data-testid="attestation-heading"
              className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight mb-4"
            >
              Independent Verification
            </h1>
            <p data-testid="attestation-statement" className="text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
              JCAM assets are independently verified via a quantum-resistant ledger.
              Every verification cycle produces a cryptographic attestation that can be
              audited without exposing underlying asset data.
            </p>
          </div>
        </ScrollReveal>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
          </div>
        ) : data && (
          <>
            {/* Status Card */}
            <ScrollReveal delay={0.1}>
              <div data-testid="attestation-status-card" className="border border-slate-800 bg-[#0A1325]/40 p-8 md:p-10 mb-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </motion.div>
                    <span data-testid="attestation-status" className="text-sm text-emerald-400 font-medium tracking-wide">
                      Verified
                    </span>
                  </div>
                  <button
                    data-testid="attestation-refresh-btn"
                    onClick={fetchAttestation}
                    className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1.5 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>

                {/* Current Hash */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-600">
                      Current Attestation Hash
                    </p>
                    <button
                      data-testid="attestation-toggle-hash"
                      onClick={() => setShowFull(!showFull)}
                      className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1.5 transition-colors"
                    >
                      {showFull ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {showFull ? "Hide" : "Show Full"}
                    </button>
                  </div>
                  <div className="bg-[#050B14] border border-slate-800 p-5 font-mono">
                    <p data-testid="attestation-hash" className="text-sm text-[#D4AF37] break-all leading-relaxed">
                      {showFull ? data.primary_hash : truncateHash(data.primary_hash)}
                    </p>
                  </div>
                </div>

                {/* Verification Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-1">Algorithm</p>
                    <p className="text-xs text-slate-300">{data.algorithm}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-1">Verification Cycle</p>
                    <p className="text-xs text-slate-300">{data.verification_cycle}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-1">Last Verified</p>
                    <p className="text-xs text-slate-300">{formatTime(data.last_verified)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-1">Next Verification</p>
                    <p className="text-xs text-slate-300">{formatTime(data.next_verification)}</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Ledger Metrics */}
            <ScrollReveal delay={0.2}>
              <div className="grid grid-cols-3 gap-px bg-slate-800 border border-slate-800 mb-8">
                {[
                  { value: data.ledger_blocks?.toLocaleString(), label: "Ledger Blocks" },
                  { value: data.consensus_nodes, label: "Consensus Nodes" },
                  { value: "Hourly", label: "Verification Cycle" },
                ].map((m) => (
                  <div key={m.label} className="bg-[#050B14] p-6 text-center">
                    <span className="block font-serif text-xl font-bold text-white">{m.value}</span>
                    <span className="text-[9px] tracking-[0.15em] uppercase text-slate-500 mt-1 block">{m.label}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Expandable Merkle Root */}
            <ScrollReveal delay={0.3}>
              <details className="group" data-testid="attestation-merkle-details">
                <summary className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 cursor-pointer transition-colors mb-4">
                  <Lock className="w-3 h-3" />
                  <span className="tracking-wide">Advanced: Merkle Root & Cross-Verification</span>
                </summary>
                <div className="border border-slate-800 bg-[#0A1325]/30 p-6 space-y-4">
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-2">Merkle Root</p>
                    <p className="font-mono text-xs text-slate-400 break-all">{data.merkle_root}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-slate-600 mb-2">Secondary Verification (SHA3-512)</p>
                    <p className="font-mono text-xs text-slate-400 break-all">{data.secondary_hash}</p>
                  </div>
                </div>
              </details>
            </ScrollReveal>

            {/* Explanation */}
            <ScrollReveal delay={0.35}>
              <div className="border-t border-slate-800 pt-8 mt-8">
                <p className="text-xs tracking-[0.15em] uppercase text-slate-600 mb-4">
                  How It Works
                </p>
                <div className="space-y-3">
                  {[
                    "Every hour, JCAM's sovereign ledger generates a new cryptographic attestation using SHA3-256 and CRYSTALS-Kyber-1024 parameters.",
                    "The attestation hash verifies that all custodied assets match their expected state without revealing any underlying data.",
                    "47 independent consensus nodes validate each cycle, ensuring no single point of failure or manipulation.",
                    "All attestation hashes are immutable and can be independently audited by any qualified party.",
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-px bg-[#D4AF37] mt-[9px] flex-shrink-0" />
                      <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* CTA */}
            <ScrollReveal delay={0.4}>
              <div className="text-center mt-12">
                <button
                  data-testid="attestation-contact-cta"
                  onClick={() => navigate("/")}
                  className="border border-slate-700 text-slate-300 px-8 py-3.5 text-xs tracking-[0.15em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                >
                  Request Full Audit Report
                </button>
              </div>
            </ScrollReveal>
          </>
        )}
      </div>
    </div>
  );
}
